import { useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { ScheduleImportResult } from '../../types/collection-targets'
import {
  CloseFooter,
  DownloadTemplate,
  DropZone,
  ErrorBanner,
  Header,
  ResultPanel,
} from './schedule-import-modal/schedule-import-modal.parts'

interface ScheduleImportModalProps {
  open: boolean
  onClose: () => void
  importing: boolean
  downloading: boolean
  result: ScheduleImportResult | null
  error: string | null
  onImport: (file: File) => void
  onDownloadTemplate: () => void
  onClearResult: () => void
}

export function ScheduleImportModal(props: ScheduleImportModalProps): ReactElement | null {
  const {
    open,
    onClose,
    importing,
    downloading,
    result,
    error,
    onImport,
    onDownloadTemplate,
    onClearResult,
  } = props
  const { t } = useTranslation('cases')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleDrop = (event: React.DragEvent): void => {
    event.preventDefault()
    setDragOver(false)
    const file = event.dataTransfer.files[0]
    if (file) onImport(file)
  }

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (file) onImport(file)
    event.target.value = ''
  }

  if (!open) return null

  return (
    <div className="items-centre justify-centre fixed inset-0 z-50 flex bg-black/40">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <Header onClose={onClose} t={t} />
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          {t('collections.scheduleImport.description')}
        </p>
        <DownloadTemplate downloading={downloading} onDownloadTemplate={onDownloadTemplate} t={t} />
        {result && <ResultPanel result={result} onImportAnother={onClearResult} t={t} />}
        {error && <ErrorBanner error={error} />}
        {!result && (
          <DropZone
            importing={importing}
            dragOver={dragOver}
            setDragOver={setDragOver}
            fileInputRef={fileInputRef}
            onDrop={handleDrop}
            onFileInput={handleFileInput}
            t={t}
          />
        )}
        <CloseFooter onClose={onClose} t={t} />
      </div>
    </div>
  )
}
